import React, { useCallback, useMemo, useState } from 'react';
import Button from './core/Button';
import { IconCheck, IconExclamationTriangle, IconXmark } from '@/icons';
import Input from './core/Input';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { cn } from '@/helpers/classnames';
import { groupActiveErrorReportsByEntityId } from '@/helpers/error_report_helpers';
import { useErrorReports } from '@/hooks/useErrorReports';

const VISIBLE_ENTITIES_LIMIT = 100;

const entityMatchesQuery = (entity: Entity, query: string): boolean => {
  const q = query.toLowerCase();
  const name = String(entity.properties['meta.name'] ?? '').toLowerCase();
  const metaId = String(entity.properties['meta.id'] ?? '').toLowerCase();
  return (
    name.includes(q) ||
    metaId.includes(q) ||
    entity.source_id.toLowerCase().includes(q) ||
    entity.full_name.toLowerCase().includes(q) ||
    entity.stop_letter.toLowerCase().includes(q)
  );
};

const messages = defineMessages({
  activeErrorReports: {
    id: 'entity_search.active_error_reports',
    defaultMessage:
      '{count, plural, one {# active error report} other {# active error reports}}',
  },
  searchPlaceholder: {
    id: 'entity_search.search_placeholder',
    defaultMessage: 'Search name or id',
  },
});

interface Props {
  options: Entity[];
  onChange: (entities: string[]) => void;
  selectedEntityIds: string[];
  className?: string;
  hideLabel?: boolean;
  /** Active error reports keyed by entity id. When omitted, loaded internally. */
  errorReportsByEntityId?: Record<string, ErrorReport[]>;
}

const EntityErrorReportWarning: React.FC<{ reports?: ErrorReport[] }> = ({
  reports,
}) => {
  const intl = useIntl();
  if (!reports || reports.length === 0) {
    return null;
  }
  const label = intl.formatMessage(messages.activeErrorReports, {
    count: reports.length,
  });
  return (
    <>
      <span
        className="inline-flex size-4 shrink-0 items-center justify-center"
        title={label}
        data-testid="entity-error-report-warning"
      >
        <IconExclamationTriangle className="size-4 text-red-500" aria-hidden />
      </span>
      <span className="sr-only">{label}</span>
    </>
  );
};

export const EntitySearch: React.FC<Props> = ({
  options,
  onChange,
  selectedEntityIds,
  className,
  hideLabel,
  errorReportsByEntityId: errorReportsByEntityIdProp,
}) => {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState('');
  const { list: errorReports } = useErrorReports();

  const errorReportsByEntityIdFromList = useMemo(
    () => groupActiveErrorReportsByEntityId(errorReports),
    [errorReports],
  );

  const errorReportsByEntityId =
    errorReportsByEntityIdProp ?? errorReportsByEntityIdFromList;

  const optionsById = useMemo(() => {
    const byId = new Map<string, Entity>();
    for (const option of options) {
      byId.set(option.id, option);
    }
    return byId;
  }, [options]);

  const selectedEntityIdSet = useMemo(
    () => new Set(selectedEntityIds),
    [selectedEntityIds],
  );

  const query = inputValue.trim();

  const filteredOptions = useMemo(() => {
    if (!query) {
      return options;
    }
    return options.filter((option) => entityMatchesQuery(option, query));
  }, [options, query]);

  const matchingSelectedIds = useMemo(() => {
    if (!query) {
      return selectedEntityIds;
    }
    return selectedEntityIds.filter((id) => {
      const entity = optionsById.get(id);
      return entity ? entityMatchesQuery(entity, query) : false;
    });
  }, [optionsById, query, selectedEntityIds]);

  const visibleSelectedEntities = useMemo(() => {
    const visible: Entity[] = [];
    for (const id of matchingSelectedIds) {
      const entity = optionsById.get(id);
      if (!entity) {
        continue;
      }
      visible.push(entity);
      if (visible.length === VISIBLE_ENTITIES_LIMIT) {
        break;
      }
    }
    return visible;
  }, [matchingSelectedIds, optionsById]);

  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      return (a.properties['meta.name'] as string).localeCompare(
        b.properties['meta.name'] as string,
      );
    });
  }, [filteredOptions]);

  const handleEntityClick = useCallback(
    (entityId: string) => {
      if (selectedEntityIdSet.has(entityId)) {
        onChange(selectedEntityIds.filter((id) => id !== entityId));
      } else {
        onChange([...selectedEntityIds, entityId]);
      }
    },
    [onChange, selectedEntityIdSet, selectedEntityIds],
  );

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {/* This div is needed since Input has grow */}
      <div>
        <Input
          id="stop-search"
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          label={
            hideLabel
              ? undefined
              : intl.formatMessage({
                  id: 'stops',
                  defaultMessage: 'Stops',
                })
          }
        />
      </div>

      <div className="shrink-0 rounded-md border border-primary-400 bg-primary-50 p-2">
        <div className="mb-1 text-xs font-medium text-primary-800">
          <FormattedMessage
            id="selected_stops"
            defaultMessage="Selected stops"
          />{' '}
          ({selectedEntityIds.length})
        </div>
        <div className="flex max-h-[100px] flex-wrap gap-1 overflow-y-auto">
          {selectedEntityIds.length === 0 && (
            <i className="text-sm text-text-700">
              <FormattedMessage
                id="no_stops_selected"
                defaultMessage="No stops selected"
              />
            </i>
          )}
          {visibleSelectedEntities.map((entity) => (
            <Button
              key={entity.id}
              variant="filled"
              size="sm"
              endIcon={<IconXmark />}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm"
              onClick={() => handleEntityClick(entity.id)}
            >
              <EntityErrorReportWarning
                reports={errorReportsByEntityId[entity.id]}
              />
              {entity.full_name}
            </Button>
          ))}
        </div>
        {matchingSelectedIds.length > VISIBLE_ENTITIES_LIMIT && (
          <p className="mt-1 text-center text-sm text-text-600">
            <FormattedMessage
              id="edit_workorder_dialog.maximum_entities_rendered"
              defaultMessage="... (only showing the first 100 items)"
            />
          </p>
        )}
      </div>

      <ul className="grow overflow-y-auto bg-background-100 p-1">
        {sortedOptions.slice(0, VISIBLE_ENTITIES_LIMIT).map((option) => {
          const isSelected = selectedEntityIdSet.has(option.id);
          return (
            <li key={option.id}>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => handleEntityClick(option.id)}
              >
                <div
                  className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                    isSelected
                      ? 'border-primary-600 bg-primary-600'
                      : 'border-text-400 bg-white'
                  }`}
                >
                  {isSelected && (
                    <IconCheck aria-hidden className="size-3 text-white" />
                  )}
                </div>
                <span className="mr-auto">
                  {option.properties['meta.name'] as string}{' '}
                  {option.stop_letter}
                </span>
                <EntityErrorReportWarning
                  reports={errorReportsByEntityId[option.id]}
                />
                {option.properties['meta.active'] === false && (
                  <span className="min-w-0 shrink-0 truncate text-sm font-semibold leading-6 text-text-800">
                    <FormattedMessage id="inactive" defaultMessage="Inactive" />
                  </span>
                )}
                <span className="text-sm text-text-700">
                  {option.source_id}
                </span>
              </Button>
            </li>
          );
        })}
        {sortedOptions.length > VISIBLE_ENTITIES_LIMIT && (
          <p className="text-center text-sm text-text-600">
            <FormattedMessage
              id="edit_workorder_dialog.maximum_entities_rendered"
              defaultMessage="... (only showing the first 100 items)"
            />
          </p>
        )}
      </ul>
    </div>
  );
};
